<template>
  <div class="health-page">
    <div class="health-backdrop"></div>

    <main class="health-shell">
      <router-link to="/" class="back-link">
        <LucideIcon name="arrow-left" :size="18" />
        <span>返回首页</span>
      </router-link>

      <section class="health-header">
        <div>
          <div class="eyebrow">
            <span class="live-dot" :class="{ checking: checking }"></span>
            <span>{{ checking ? '检测中' : '节点健康' }}</span>
          </div>
          <h1>延迟健康图</h1>
        </div>
        <button class="check-button" :disabled="checking" @click="runHealthCheck" title="立即执行一次延迟检测">
          <LucideIcon name="refresh-cw" :size="17" :class="{ spinning: checking }" />
          <span>{{ checking ? '检测中...' : '立即检测' }}</span>
        </button>
      </section>

      <section class="stats-row" v-if="health">
        <div class="stat">
          <span class="stat-label">节点</span>
          <strong>{{ health.total }}</strong>
        </div>
        <div class="stat">
          <span class="stat-label">可用</span>
          <strong>{{ health.healthy }}</strong>
        </div>
        <div class="stat">
          <span class="stat-label">慢速</span>
          <strong>{{ health.degraded }}</strong>
        </div>
        <div class="stat">
          <span class="stat-label">不可达</span>
          <strong>{{ health.down }}</strong>
        </div>
        <div class="stat stat-wide">
          <span class="stat-label">平均延迟</span>
          <strong>{{ formatLatency(health.averageLatencyMs) }}</strong>
        </div>
      </section>

      <section class="heatmap-panel" :class="{ loading: loading }">
        <div v-if="loading" class="panel-empty">
          <div class="spinner-sm"></div>
          <span>加载中...</span>
        </div>
        <div v-else-if="error" class="panel-empty error">
          <LucideIcon name="x-circle" :size="18" />
          <span>{{ error }}</span>
        </div>
        <div v-else-if="nodes.length === 0" class="panel-empty">
          <LucideIcon name="signal" :size="18" />
          <span>暂无可检测节点</span>
        </div>
        <div v-else class="heatmap-scroll">
          <div class="heatmap-grid" :style="{ '--rows': gridRows }">
            <button
              v-for="node in nodes"
              :key="node.id"
              type="button"
              class="health-cell"
              :class="`is-${node.status}`"
              :title="getNodeTitle(node)"
              :aria-label="getNodeTitle(node)"
            ></button>
          </div>
        </div>
      </section>

      <section class="health-meta" v-if="health">
        <div class="legend">
          <span class="legend-label">延迟</span>
          <span class="legend-chip is-down"></span>
          <span class="legend-chip is-slow"></span>
          <span class="legend-chip is-ok"></span>
          <span class="legend-chip is-fast"></span>
        </div>
        <div class="time-row">
          <span>上次：{{ formatTime(health.updatedAt) }}</span>
          <span>下次：{{ formatTime(health.nextCheckAt) }}</span>
        </div>
      </section>

      <section class="node-list" v-if="attentionNodes.length">
        <div class="list-header">
          <LucideIcon name="signal" :size="15" />
          <span>需要关注</span>
        </div>
        <div class="node-row" v-for="node in attentionNodes" :key="node.id">
          <span class="node-name">{{ node.name }}</span>
          <span class="node-status" :class="`is-${node.status}`">{{ getStatusText(node) }}</span>
        </div>
      </section>
    </main>
  </div>
</template>

<script>
export default {
  name: 'NodeHealth',
  data() {
    return {
      health: null,
      loading: true,
      checking: false,
      error: '',
      refreshTimer: null,
      gridRows: 7
    }
  },
  computed: {
    nodes() {
      return this.health?.nodes || [];
    },
    attentionNodes() {
      return this.nodes
        .filter(node => node.status === 'slow' || node.status === 'down')
        .slice(0, 12);
    }
  },
  methods: {
    async fetchHealth() {
      try {
        const res = await this.$axios.get('/api/node-health');
        this.health = res.data;
        this.checking = !!res.data.checking;
        this.error = '';
      } catch (err) {
        this.error = err.response?.data?.error || '健康状态读取失败';
      } finally {
        this.loading = false;
      }
    },
    async runHealthCheck() {
      if (this.checking) return;
      this.checking = true;
      this.error = '';
      try {
        const res = await this.$axios.post('/api/node-health/check');
        this.health = res.data;
      } catch (err) {
        this.error = err.response?.data?.error || '健康检测失败';
      } finally {
        this.checking = false;
      }
    },
    formatLatency(latencyMs) {
      return Number.isFinite(latencyMs) ? `${latencyMs}ms` : '-';
    },
    formatTime(isoStr) {
      if (!isoStr) return '-';
      const d = new Date(isoStr);
      if (Number.isNaN(d.getTime())) return '-';
      const pad = n => String(n).padStart(2, '0');
      return `${pad(d.getMonth() + 1)}/${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    },
    getStatusText(node) {
      if (node.status === 'fast') return `${this.formatLatency(node.latencyMs)} · 快`;
      if (node.status === 'ok') return `${this.formatLatency(node.latencyMs)} · 稳`;
      if (node.status === 'slow') return `${this.formatLatency(node.latencyMs)} · 慢`;
      return node.error || '不可达';
    },
    getNodeTitle(node) {
      return `${node.name} / ${node.type} / ${this.getStatusText(node)}`;
    }
  },
  mounted() {
    this.fetchHealth();
    this.refreshTimer = setInterval(this.fetchHealth, 30000);
  },
  beforeDestroy() {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
      this.refreshTimer = null;
    }
  }
}
</script>

<style scoped>
.health-page {
  position: relative;
  min-height: 100vh;
  width: 100vw;
  overflow-x: hidden;
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.04), transparent 34%),
    radial-gradient(circle at 50% -10%, rgba(76, 175, 80, 0.16), transparent 34%),
    #030504;
  color: #f8fbff;
  font-family: var(--font-body);
}

.health-backdrop {
  position: fixed;
  inset: 0;
  pointer-events: none;
  background-image:
    linear-gradient(rgba(255, 255, 255, 0.035) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.035) 1px, transparent 1px);
  background-size: 42px 42px;
  mask-image: linear-gradient(to bottom, #000, transparent 82%);
}

.health-shell {
  position: relative;
  z-index: 1;
  width: min(1180px, calc(100vw - 40px));
  min-height: 100vh;
  margin: 0 auto;
  padding: 32px 0 44px;
  text-align: left;
}

.back-link {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 12px;
  color: rgba(255, 255, 255, 0.72);
  text-decoration: none;
  transition: all 0.25s ease;
}

.back-link:hover {
  border-color: rgba(132, 225, 157, 0.4);
  color: #fff;
  transform: translateX(-3px);
}

.health-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 20px;
  margin: 42px 0 24px;
}

.eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: rgba(248, 251, 255, 0.58);
  font-size: 0.86rem;
  font-weight: 700;
}

.live-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #43c46f;
  box-shadow: 0 0 0 6px rgba(67, 196, 111, 0.12);
}

.live-dot.checking {
  animation: pulse 1s ease-in-out infinite;
}

@keyframes pulse {
  50% { transform: scale(1.35); }
}

.health-header h1 {
  margin: 10px 0 0;
  font-size: clamp(2rem, 6vw, 5.2rem);
  line-height: 0.95;
  letter-spacing: 0;
  font-weight: 800;
}

.check-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 44px;
  padding: 0 18px;
  border-radius: 12px;
  border: 1px solid rgba(132, 225, 157, 0.45);
  background: rgba(132, 225, 157, 0.12);
  color: #dfffe8;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.25s ease;
}

.check-button:hover:not(:disabled) {
  background: rgba(132, 225, 157, 0.2);
  transform: translateY(-1px);
}

.check-button:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.spinning {
  animation: spin 0.85s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.stats-row {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 10px;
  margin-bottom: 22px;
}

.stat {
  min-width: 0;
  padding: 14px 16px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.055);
}

.stat-label {
  display: block;
  margin-bottom: 6px;
  color: rgba(248, 251, 255, 0.5);
  font-size: 0.78rem;
}

.stat strong {
  color: #fff;
  font-size: 1.28rem;
}

.heatmap-panel {
  min-height: 164px;
  padding: 16px;
  border-radius: 30px;
  background: #fafbfd;
  box-shadow:
    inset 0 0 0 1px rgba(4, 8, 6, 0.06),
    0 24px 80px rgba(0, 0, 0, 0.42);
}

.heatmap-scroll {
  width: 100%;
  overflow-x: auto;
  padding-bottom: 4px;
}

.heatmap-grid {
  display: grid;
  grid-template-rows: repeat(var(--rows), 18px);
  grid-auto-flow: column;
  grid-auto-columns: 18px;
  gap: 7px;
  width: max-content;
  min-width: 100%;
}

.health-cell {
  width: 18px;
  height: 18px;
  padding: 0;
  border: 1px solid rgba(15, 23, 42, 0.06);
  border-radius: 4px;
  appearance: none;
  cursor: default;
  transition: transform 0.18s ease, box-shadow 0.18s ease, filter 0.18s ease;
}

.health-cell:focus-visible {
  outline: 2px solid #0f172a;
  outline-offset: 2px;
}

.health-cell:hover {
  transform: scale(1.25);
  box-shadow: 0 7px 16px rgba(0, 0, 0, 0.2);
  z-index: 2;
}

.health-cell.is-fast,
.legend-chip.is-fast {
  background: #116b2f;
}

.health-cell.is-ok,
.legend-chip.is-ok {
  background: #44bf6e;
}

.health-cell.is-slow,
.legend-chip.is-slow {
  background: #a9edb8;
}

.health-cell.is-down,
.legend-chip.is-down {
  background: #e8ebef;
}

.panel-empty {
  min-height: 132px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  color: #7a838f;
  font-weight: 700;
}

.panel-empty.error {
  color: #d45b5b;
}

.spinner-sm {
  width: 18px;
  height: 18px;
  border: 2px solid rgba(17, 107, 47, 0.16);
  border-top-color: #116b2f;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.health-meta {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  margin-top: 18px;
  color: rgba(248, 251, 255, 0.58);
  font-size: 0.86rem;
}

.legend,
.time-row {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.legend-label {
  color: rgba(248, 251, 255, 0.48);
}

.legend-chip {
  width: 18px;
  height: 18px;
  border-radius: 4px;
  border: 1px solid rgba(255, 255, 255, 0.12);
}

.node-list {
  margin-top: 28px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding-top: 16px;
}

.list-header {
  display: flex;
  align-items: center;
  gap: 7px;
  margin-bottom: 10px;
  color: rgba(248, 251, 255, 0.62);
  font-weight: 700;
}

.node-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 16px;
  padding: 10px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.07);
}

.node-name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.node-status {
  color: rgba(248, 251, 255, 0.7);
  font-family: var(--font-mono);
  font-size: 0.82rem;
}

.node-status.is-slow {
  color: #c8ff9d;
}

.node-status.is-down {
  color: #c7cdd6;
}

@media (max-width: 720px) {
  .health-shell {
    width: min(100vw - 28px, 680px);
    padding-top: 22px;
  }

  .health-header {
    align-items: flex-start;
    flex-direction: column;
    margin-top: 34px;
  }

  .check-button {
    width: 100%;
  }

  .stats-row {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .stat-wide {
    grid-column: span 2;
  }

  .heatmap-panel {
    border-radius: 24px;
    padding: 12px;
  }

  .heatmap-grid {
    grid-template-rows: repeat(var(--rows), 15px);
    grid-auto-columns: 15px;
    gap: 5px;
  }

  .health-cell,
  .legend-chip {
    width: 15px;
    height: 15px;
  }

  .health-meta {
    flex-direction: column;
  }
}
</style>
